/* eslint-disable no-alert */
/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  createContext,
  useMemo,
  useReducer,
  useEffect,
  useContext,
} from 'react';
import produce from 'immer';
import { useIntl } from 'react-intl';
import { formatDateMMDDYYYY } from '@mummssoftware/utils/formatters';
import { AppContext } from '../../App/AppContext';
import { CertContext } from '../../../Context';
import {
  SET_RAW_REVIEWS,
  SET_FORMATTED_REVIEWS,
  SET_PATIENT_REVIEWS_MAP,
  SET_REVIEW_TYPE,
  REVIEW_OPEN,
  REVIEWED,
  SET_FROM_DATE,
  SET_TO_DATE,
} from '../../../constants';
import { compileReviews, makePatientReviewsMap } from '../../utils/utils';

const initReviewContext = {
  reloadReviewGrid: null,
  rawReviews: null,
  formattedReviews: null,
  patientReviewsMap: null,
  reviewType: null,
};

// maybe change this to add a new data variable (to be called further down in ApprovalListingGrid)
const ReviewContextReducer = (draft, action) => {
  switch (action.type) {
    case SET_RAW_REVIEWS:
      draft.rawReviews = action.payload;
      break;
    case SET_FORMATTED_REVIEWS:
      draft.formattedReviews = action.payload;
      break;
    case SET_PATIENT_REVIEWS_MAP:
      draft.patientReviewsMap = action.payload;
      break;
    case SET_REVIEW_TYPE:
      draft.reviewType = action.payload;
      break;
    default:
  }
};

const curriedContextReducer = produce(ReviewContextReducer);

export const ReviewContext = createContext(initReviewContext);
export const ReviewDispatchContext = createContext({});
export const ReviewContextConsumer = ReviewContext.Consumer;
export const ReviewDispatchContextConsumer = ReviewDispatchContext.Consumer;

export const ReviewContextProvider = ({ children }) => {
  const { userId, agency } = useContext(AppContext);
  const { workflow } = useContext(CertContext);
  const { formatMessage } = useIntl();

  const reloadReviewGrid = () => {
    reviewDispatch({
      type: SET_RAW_REVIEWS,
      payload: null,
    });
    reviewDispatch({
      type: SET_REVIEW_TYPE,
      payload: null,
    });
    fetchData();
  };

  const createInitialState = () => ({
    rawReviews: null,
    formattedReviews: null,
    patientReviewsMap: null,
    reviewType: null,
    reloadReviewGrid,
  });

  const [reviewState, reviewDispatch] = useReducer(
    curriedContextReducer,
    createInitialState(),
  );

  const fetchData = () => {
    try {
      switch (localStorage.getItem(SET_REVIEW_TYPE)) {
        case REVIEW_OPEN:
          (async () => {
            workflow
              .getCurrentReviews(userId, undefined, agency)
              .then((rawReviews) => {
                reviewDispatch({
                  type: SET_RAW_REVIEWS,
                  payload: rawReviews,
                });
              })
              .finally(() =>
                reviewDispatch({
                  type: SET_REVIEW_TYPE,
                  payload: localStorage.getItem(SET_REVIEW_TYPE),
                }),
              );
          })();
          break;
        case REVIEWED:
          (async () => {
            workflow
              .getOldReviews(
                userId,
                formatDateMMDDYYYY(
                  new Date(localStorage.getItem(SET_FROM_DATE)),
                ),
                formatDateMMDDYYYY(new Date(localStorage.getItem(SET_TO_DATE))),
                undefined,
                agency,
              )
              .then(
                (rawReviews) => {
                  reviewDispatch({
                    type: SET_RAW_REVIEWS,
                    payload: rawReviews,
                  });
                },
                () =>
                  reviewDispatch({
                    type: SET_RAW_REVIEWS,
                    payload: null,
                  }),
              )
              .finally(() =>
                reviewDispatch({
                  type: SET_REVIEW_TYPE,
                  payload: localStorage.getItem(SET_REVIEW_TYPE),
                }),
              );
          })();
          break;
        default:
      }
    } catch (error) {
      const { status } = error && error?.response ? error?.response : error;
      if (status === 401) {
        window.alert(
          formatMessage({
            id: 'authError',
          }),
        );
      }
      // certError is Something went wrong, please try again.
      window.alert(
        formatMessage({
          id: 'certError',
        }),
      );
    }
  };

  useEffect(() => {
    const { rawReviews } = reviewState;
    const patientReviewsMap = makePatientReviewsMap(rawReviews);
    reviewDispatch({
      type: SET_PATIENT_REVIEWS_MAP,
      payload: patientReviewsMap,
    });
  }, [reviewState.rawReviews]);

  useEffect(() => {
    const { patientReviewsMap } = reviewState;
    const compiledReviews = compileReviews(patientReviewsMap);
    reviewDispatch({
      type: SET_FORMATTED_REVIEWS,
      payload: compiledReviews,
    });
  }, [reviewState.patientReviewsMap]);

  const DispatchValue = useMemo(
    () => ({
      reviewDispatch,
    }),
    [reviewDispatch],
  );

  return (
    <ReviewContext.Provider value={reviewState}>
      <ReviewDispatchContext.Provider value={DispatchValue}>
        {children}
      </ReviewDispatchContext.Provider>
    </ReviewContext.Provider>
  );
};

// returns a react component (hence accept props)
// called by Listing
export function withReviewContext(Component) {
  return function contextComponent(props) {
    return (
      <ReviewContextConsumer>
        {(context) => (
          <ReviewDispatchContextConsumer>
            {(dispatchContext) => (
              <Component {...props} {...context} {...dispatchContext} />
            )}
          </ReviewDispatchContextConsumer>
        )}
      </ReviewContextConsumer>
    );
  };
}
